/**
 * EnglishTextModal - Collects English transcript text from the user.
 * Includes word count display and minimum 10-word validation.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';

interface EnglishTextModalProps {
  initialText?: string;
  onSubmit: (text: string) => void;
  onBack: () => void;
  onClose: () => void;
}

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

const MIN_WORDS = 10;

const EnglishTextModal: React.FC<EnglishTextModalProps> = ({
  initialText = '',
  onSubmit,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [text, setText] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const wordCount = countWords(text);
  const isValid = wordCount >= MIN_WORDS;

  useEffect(() => {
    // Auto-focus the textarea on mount
    textareaRef.current?.focus();
  }, []);

  const handleContinue = () => {
    if (isValid) {
      onSubmit(text.trim());
    }
  };

  return (
    <ModalShell
      title={t('creator:workflow.resourceModal.englishTitle')}
      onClose={onClose}
      onBack={onBack}
      onNext={handleContinue}
      nextDisabled={!isValid}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('creator:workflow.stage0.textPlaceholder')}
          style={{
            width: '100%',
            minHeight: '200px',
            padding: '14px',
            borderRadius: '10px',
            border: '1px solid var(--border-color, #d1d5db)',
            fontSize: '0.95rem',
            lineHeight: '1.6',
            resize: 'vertical',
            fontFamily: 'inherit',
            color: 'var(--text-primary, #1a1a2e)',
            background: 'var(--bg-primary, #fff)',
            transition: 'border-color 0.15s',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--primary-color, #6366f1)';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-color, #d1d5db)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.82rem',
            color: wordCount < MIN_WORDS
              ? 'var(--text-warning, #d97706)'
              : 'var(--text-secondary, #6b7280)',
          }}
        >
          <span>
            {t('creator:workflow.resourceModal.wordCount', { count: wordCount })}
          </span>
          {wordCount > 0 && wordCount < MIN_WORDS && (
            <span style={{ fontStyle: 'italic' }}>
              min {MIN_WORDS}
            </span>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default EnglishTextModal;
